<div class="p-4">
  <div class="mb-6 flex flex-wrap items-center justify-between gap-4">
    <h1 class="text-2xl font-semibold">Product Squash Planner</h1>
    <div class="flex flex-wrap gap-2">
      <button class="btn btn-ghost" type="button" (click)="clearSession()">
        Clear all
      </button>
      <button
        class="btn btn-primary"
        type="button"
        [disabled]="savedGroups.length === 0"
        (click)="exportJson()"
      >
        Export JSON
      </button>
    </div>
  </div>

  <div class="alert alert-info mb-6">
    Pick a primary → add children → preview (optional) → save group → repeat for
    next primary. All groups persist automatically. Remove a primary with ✕ if
    added by mistake.
  </div>

  @if (error) {
    <div class="alert alert-error mb-6">{{ error }}</div>
  }

  <label class="label mb-6 cursor-pointer justify-start gap-3">
    <input
      type="checkbox"
      class="checkbox"
      [(ngModel)]="hideAlreadySelected"
      (ngModelChange)="onHideAlreadySelectedChange()"
    />
    <span class="label-text text-base"
      >Hide already selected products (saved as primary or child)</span
    >
  </label>

  @if (savedGroups.length > 0) {
    <section class="mb-8">
      <h2 class="mb-4 text-xl font-medium">Saved primary groups</h2>
      <div class="flex flex-col gap-3">
        @for (group of savedGroups; track group.primary_product.id) {
          <div
            class="card border shadow-sm"
            [ngClass]="
              isActivePrimary(group.primary_product.id)
                ? 'border-primary bg-primary/5'
                : ''
            "
          >
            <div
              class="card-body flex-row flex-wrap items-center justify-between gap-3 p-4"
            >
              <div>
                <p class="font-medium">
                  {{ group.primary_product.code }} —
                  {{ group.primary_product.title }}
                </p>
                <p class="text-sm text-base-content/70">
                  Variance {{ group.primary_product.variance_code }} ·
                  {{ group.child_product_ids.length }} children
                  @if (group.preview) {
                    · {{ group.preview.matched_count }} matched
                  }
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                @if (!isActivePrimary(group.primary_product.id)) {
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    (click)="editPrimaryGroup(group.primary_product.id)"
                  >
                    Edit
                  </button>
                } @else {
                  <span class="badge badge-primary">Editing</span>
                }
                <button
                  type="button"
                  class="btn btn-ghost btn-sm text-error"
                  (click)="removePrimaryGroup(group.primary_product.id, $event)"
                >
                  Remove primary
                </button>
              </div>
            </div>
          </div>
        }
      </div>
    </section>
  }

  @if (activePrimary) {
    <div class="card mb-8 border border-base-300 bg-base-200 shadow-sm">
      <div class="card-body gap-4 p-4">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 class="card-title text-lg">Editing primary</h2>
            <p class="text-base font-medium">
              {{ activePrimary.code }} — {{ activePrimary.title }}
              <span class="text-base-content/70"
                >(variance {{ activePrimary.variance_code }})</span
              >
            </p>
          </div>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            [disabled]="
              !activeGroup || activeGroup.child_product_ids.length === 0
            "
            (click)="saveCurrentGroup()"
          >
            Save group & pick next primary
          </button>
        </div>
        @if (getChildProductsForActive().length > 0) {
          <div>
            <p class="mb-2 text-xs font-medium uppercase text-base-content/60">
              Children for this primary
            </p>
            <div class="flex flex-wrap gap-2">
              @for (child of getChildProductsForActive(); track child.id) {
                <span class="badge badge-lg gap-2 pr-1">
                  {{ child.code }} — {{ child.title }}
                  <button
                    type="button"
                    class="btn btn-circle btn-ghost btn-xs"
                    (click)="removeChildFromActive(child.id)"
                    aria-label="Remove child"
                  >
                    ✕
                  </button>
                </span>
              }
            </div>
          </div>
        } @else {
          <p class="text-sm text-base-content/70">
            No children selected for this primary yet.
          </p>
        }
      </div>
    </div>
  }

  <section class="mb-10">
    <h2 class="mb-4 text-xl font-medium">1. Select primary product</h2>
    <p class="mb-4 text-sm text-base-content/70">
      Click a row to work on that primary. Checkmark = saved primary. Child
      badge = already a child under another primary. Highlight = editing.
    </p>

    <input
      type="text"
      class="input input-bordered mb-4 w-full max-w-md"
      placeholder="Search primary by code, title, variance, type"
      [(ngModel)]="primarySearchText"
      (input)="updatePrimaryDisplayProducts()"
      [disabled]="loadingProducts"
    />

    @if (loadingProducts) {
      <span class="loading loading-spinner loading-lg"></span>
    } @else {
      <div class="overflow-x-auto rounded-lg border border-solid border-black">
        <table class="table table-zebra">
          <thead>
            <tr>
              <th class="w-12"></th>
              <th class="w-12"></th>
              <th>Code</th>
              <th>Title</th>
              <th>Variance</th>
              <th>Status</th>
              <th>Type</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            @for (product of displayPrimaryProducts; track product.id) {
              <tr
                class="cursor-pointer"
                [ngClass]="{
                  'bg-primary/10': isActivePrimary(product.id),
                  'bg-base-200':
                    isSavedPrimary(product.id) && !isActivePrimary(product.id),
                }"
                (click)="selectPrimary(product)"
              >
                <td>
                  @if (isSavedPrimary(product.id)) {
                    <app-check-icon
                      class="mx-auto block h-5 w-5 text-success"
                    ></app-check-icon>
                  }
                </td>
                <td>
                  @if (isSavedPrimary(product.id)) {
                    <button
                      type="button"
                      class="btn btn-circle btn-ghost btn-xs"
                      title="Remove as primary"
                      (click)="removePrimaryGroup(product.id, $event)"
                    >
                      ✕
                    </button>
                  }
                </td>
                <td>{{ product.code }}</td>
                <td>{{ product.title }}</td>
                <td>{{ product.variance_code }}</td>
                <td>
                  @if (isChildOfAnyPrimary(product.id)) {
                    <span
                      class="badge badge-outline badge-sm whitespace-nowrap"
                      [title]="
                        'Child of primary code(s): ' +
                        childParentLabels(product.id)
                      "
                    >
                      Child
                    </span>
                  }
                </td>
                <td>{{ product.type }}</td>
                <td>{{ product.active ? 'Yes' : 'No' }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>

  <section class="mb-10">
    <h2 class="mb-4 text-xl font-medium">2. Child products</h2>
    <p class="mb-4 text-sm text-base-content/70">
      @if (activePrimary) {
        Children apply to
        <strong>{{ activePrimary.code }}</strong> only. Selections save
        automatically.
      } @else {
        Select or edit a primary above, then check children here.
      }
    </p>

    <input
      type="text"
      class="input input-bordered mb-4 w-full max-w-md"
      placeholder="Search children by code, title, variance, type"
      [(ngModel)]="childSearchText"
      (input)="updateChildDisplayProducts()"
      [disabled]="loadingProducts"
    />

    @if (loadingProducts) {
      <span class="loading loading-spinner loading-lg"></span>
    } @else {
      <div class="overflow-x-auto rounded-lg border border-solid border-black">
        <table class="table table-zebra">
          <thead>
            <tr>
              <th class="w-12"></th>
              <th>Code</th>
              <th>Title</th>
              <th>Variance</th>
              <th>Type</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            @for (product of displayChildProducts; track product.id) {
              <tr [class.bg-base-200]="isChildSelectedForActive(product.id)">
                <td>
                  <input
                    type="checkbox"
                    class="checkbox"
                    [checked]="isChildSelectedForActive(product.id)"
                    [disabled]="!activePrimary"
                    (change)="
                      toggleChild(product.id, $any($event.target).checked)
                    "
                  />
                </td>
                <td>{{ product.code }}</td>
                <td>{{ product.title }}</td>
                <td>{{ product.variance_code }}</td>
                <td>{{ product.type }}</td>
                <td>{{ product.active ? 'Yes' : 'No' }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>

  <section class="mb-10">
    <h2 class="mb-4 text-xl font-medium">3. Preview mappings</h2>
    <p class="mb-4 text-sm text-base-content/70">
      Preview applies to the primary you are currently editing.
    </p>

    <div class="mb-4 flex flex-wrap gap-2">
      <button
        class="btn btn-secondary"
        type="button"
        [disabled]="
          !activePrimary ||
          !activeGroup ||
          activeGroup.child_product_ids.length === 0 ||
          loadingPreview
        "
        (click)="runPreview()"
      >
        @if (loadingPreview) {
          <span class="loading loading-spinner loading-sm"></span>
        }
        Refresh preview
      </button>
    </div>

    @if (previewNeedsRefresh) {
      <div class="alert alert-warning mb-4">
        Saved preview was outdated (missing variance / lock fields). Click
        <strong>Refresh preview</strong> to reload.
      </div>
    }

    @if (activePreview) {
      <div class="mb-4 flex gap-4 text-sm">
        <span>Matched: {{ activePreview.matched_count }}</span>
        <span>Unmatched: {{ activePreview.unmatched_count }}</span>
      </div>

      @if (activePreview.variant_migrations.length > 0) {
        <h3 class="mb-3 font-medium">Variant migrations</h3>
        <div class="migration-cards mb-8">
          @for (
            row of activePreview.variant_migrations;
            track row.from_variant_id
          ) {
            <div class="card border border-base-300 bg-base-100 shadow-sm">
              <div class="card-body gap-4 p-4">
                <div class="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p class="text-xs uppercase text-base-content/60">
                      Child → Primary
                    </p>
                    <p class="font-medium">
                      {{ row.from_product_code }} → {{ row.to_product_code }}
                    </p>
                    <p class="text-sm text-base-content/70">
                      {{ row.from_product_title }}
                    </p>
                  </div>
                  <div class="text-right text-sm">
                    <p>{{ row.license_count }} licenses</p>
                    <p>{{ row.invoice_relation_count }} invoice relations</p>
                  </div>
                </div>

                <div class="grid gap-4 md:grid-cols-2">
                  <div class="rounded-lg bg-base-200 p-3">
                    <p
                      class="mb-2 text-xs font-semibold uppercase text-base-content/60"
                    >
                      From (child)
                    </p>
                    <p class="mb-1 font-mono text-xs text-base-content/70">
                      {{ row.from_variant_id }}
                    </p>
                    <p class="text-sm">
                      {{
                        variantAttributeLine(
                          row.from_variant,
                          row.from_product_id
                        )
                      }}
                    </p>
                    <div class="flex flex-wrap gap-2 text-sm">
                      @for (
                        line of variantPriceLines(row.from_variant);
                        track line
                      ) {
                        <span>{{ line }}</span>
                      }
                    </div>
                  </div>

                  <div class="rounded-lg bg-primary/10 p-3">
                    <p
                      class="mb-2 text-xs font-semibold uppercase text-base-content/60"
                    >
                      To (primary)
                    </p>
                    <p class="mb-1 font-mono text-xs text-base-content/70">
                      {{ row.to_variant_id }}
                    </p>
                    <p class="text-sm">
                      {{
                        variantAttributeLine(row.to_variant, row.to_product_id)
                      }}
                    </p>
                    <div class="flex flex-wrap gap-2 text-sm">
                      @for (
                        line of variantPriceLines(row.to_variant);
                        track line
                      ) {
                        <span>{{ line }}</span>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      }

      @if (activePreview.unmatched_child_variants.length > 0) {
        <h3 class="mb-3 font-medium">Unmatched child variants</h3>
        <div class="migration-cards">
          @for (
            row of activePreview.unmatched_child_variants;
            track row.variant_id
          ) {
            <div class="card border border-warning bg-base-100 shadow-sm">
              <div class="card-body gap-3 p-4">
                <div class="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p class="badge badge-warning badge-outline">
                      {{ unmatchedReasonLabel(row.reason) }}
                    </p>
                    <p class="mt-2 font-medium">
                      {{ row.product_code }} — {{ row.product_title }}
                    </p>
                    <p class="font-mono text-xs text-base-content/70">
                      {{ row.variant_id }}
                    </p>
                  </div>
                  <p class="text-right text-sm">
                    {{ row.active_license_count }} active license{{
                      row.active_license_count === 1 ? '' : 's'
                    }}
                  </p>
                </div>
                <div class="rounded-lg bg-base-200 p-3">
                  <p class="text-sm">
                    {{ variantAttributeLine(row.variant, row.product_id) }}
                  </p>
                  <div class="flex flex-wrap gap-2 text-sm">
                    @for (line of variantPriceLines(row.variant); track line) {
                      <span>{{ line }}</span>
                    }
                  </div>
                </div>
                @if (row.conflicting_variant_ids?.length) {
                  <p class="text-xs text-base-content/70">
                    Conflicting primary variants:
                    {{ row.conflicting_variant_ids?.join(', ') }}
                  </p>
                }

                @if (row.close_matches?.length) {
                  <div class="mt-2 border-t border-base-300 pt-3">
                    <p
                      class="mb-2 text-xs font-semibold uppercase text-base-content/60"
                    >
                      Close primary matches
                    </p>
                    <div class="flex flex-col gap-2">
                      @for (
                        close of row.close_matches;
                        track close.primary_variant.id
                      ) {
                        <div
                          class="rounded-lg border border-base-300 bg-base-200/60 p-3"
                        >
                          <div class="mb-2 flex flex-wrap items-center gap-2">
                            <span
                              class="badge badge-outline badge-sm whitespace-nowrap"
                            >
                              {{ closeMatchKindLabel(close) }}
                            </span>
                            <span
                              class="font-mono text-xs text-base-content/70"
                            >
                              {{ close.primary_variant.id }}
                            </span>
                          </div>
                          <p class="mb-1 text-sm">
                            {{
                              variantAttributeLine(
                                close.primary_variant,
                                activePrimary?.id
                              )
                            }}
                          </p>
                          <p class="mb-2 text-xs text-base-content/70">
                            {{ closeMatchPriceDetail(close) }}
                          </p>
                          <div class="flex flex-wrap gap-2 text-sm">
                            @for (
                              line of variantPriceLines(close.primary_variant);
                              track line
                            ) {
                              <span>{{ line }}</span>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
    } @else if (!loadingPreview) {
      @if (!activePrimary) {
        <p class="text-base-content/70">Select a primary to preview.</p>
      } @else if (!activeGroup || activeGroup.child_product_ids.length === 0) {
        <p class="text-base-content/70">Select at least one child product.</p>
      } @else {
        <p class="text-base-content/70">
          Click refresh preview to load mappings.
        </p>
      }
    }
  </section>
</div>
